import { Select } from 'antd';
import useAuth from 'common/useAuth';
import { useStorePermissions } from 'common/usePermissions';
import { CustomEmpty } from 'components';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { SelectedMaterialItem as ISelectedMaterialItem } from 'pages/material/models';
import { useGetStoresV2Query } from 'pages/material/queries';
import React, { useMemo } from 'react';

import type { MaterialToEditAmount } from '../EditAmountForm';
import SelectedMaterialItem from '../SelectedMaterialItem';
import { REMOVE_FROM_STORE_ID } from './MoveMaterialFromStoreModal';

interface Props {
  items: ISelectedMaterialItem[];
  selectedStoreId: string;
  setDestinationStoreId: (id: string) => void;
  destinationStoreId: string;
  onDelete: (materialId: string) => void;
  onEdit: (item: MaterialToEditAmount) => void;
}

const MaterialToRemoveList: React.FC<Props> = (props): React.ReactElement => {
  const { items, destinationStoreId, setDestinationStoreId, onDelete, onEdit, selectedStoreId } =
    props;

  const { t } = useCustomTranslation();
  const { can } = useStorePermissions();
  const { user } = useAuth();

  const storesQuery = useGetStoresV2Query();

  const filteredStores = useMemo(() => {
    if (can(['Stores_ManageAll', 'Stores_ManageAllQuantity'])) {
      return storesQuery.data?.filter((x) => x.id !== selectedStoreId) || [];
    } else {
      return (
        storesQuery.data?.filter(
          (x) =>
            x.id !== selectedStoreId &&
            x.availableFor.some((av) => av.userId === user?.id && av.permissionLevel === 'Manage')
        ) || []
      );
    }
  }, [can, storesQuery.data, selectedStoreId, user?.id]);

  return (
    <div className="SelectedMaterialList">
      <div className="SelectedMaterialList_head">{t('stores.materialToRemove')}</div>
      <div className="SelectedMaterialList_content">
        {items.length < 1 ? (
          <CustomEmpty description={t('stores.noOutcomeMaterialSelected')} />
        ) : (
          <>
            <div className="SelectedMaterialList_items">
              {items.map((item) => (
                <SelectedMaterialItem
                  key={item.materialId}
                  item={item}
                  onEdit={() =>
                    onEdit({
                      materialId: item.materialId,
                      amount: item.amount,
                      maxAmount: item.maxAmount
                    })
                  }
                  onDelete={() => onDelete(item.materialId)}
                />
              ))}
            </div>
            <div className="SelectedMaterialList_summary">
              <div className="SelectedMaterialList_summary-destination">
                {t('stores.destination')}
              </div>
              <Select
                value={destinationStoreId}
                onChange={(value: string) => setDestinationStoreId(value)}
                options={[
                  { label: t('stores.removeFromStoreOption'), value: REMOVE_FROM_STORE_ID },
                  ...(filteredStores.map((store) => ({
                    label: store.name,
                    value: store.id
                  })) || [])
                ]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MaterialToRemoveList;
